import { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiLinkedin } from 'react-icons/fi';

import OptimizedImage from '../OptimizedImage';

export interface TeamMember {
  id: number;
  name: string;
  role: string;
  bio: string;
  image: string;
  social: {
    linkedin?: string;
  };
}

const TeamMemberItem = ({ member }: { member: TeamMember }) => {
  const [expanded, setExpanded] = useState(false);

  // Get the snippet (first sentence or first X characters)
  const getBioSnippet = (bio: string, maxLength = 150) => {
    const firstSentenceMatch = bio.match(/^.*?[.!?]\s/); // Match first sentence ending with ., !, or ?
    const firstSentence = firstSentenceMatch
      ? firstSentenceMatch[0]
      : bio.substring(0, maxLength);

    if (firstSentence.length < bio.length) {
      return firstSentence.trim();
    }
    return bio;
  };

  const needsExpansion = member.bio.length > 150;
  const bioSnippet = getBioSnippet(member.bio);
  const buttonId = `bio-expand-${member.id}`;

  return (
    <div className="flex flex-col md:flex-row gap-6 md:gap-8 py-8 border-b border-theme last:border-0">
      {/* Left: Photo */}
      <div className="w-full md:w-1/3 lg:w-1/4 flex-shrink-0">
        <div className="relative w-full max-w-[280px] sm:max-w-[220px] lg:max-w-[240px] mx-auto md:mx-0 rounded-lg overflow-hidden bg-card dark:bg-panel/80">
          <OptimizedImage
            src={member.image}
            alt={`${member.name} - ${member.role}`}
            aspectRatio="1/1"
            sizes="(max-width: 768px) 280px, 240px"
          />
        </div>
      </div>

      {/* Right: Content */}
      <div className="w-full md:w-2/3 lg:w-3/4 flex flex-col">
        <h3 className="text-xl font-semibold text-primary dark:text-theme-primary mb-1">
          {member.name}
        </h3>
        <p className="text-secondary font-medium mb-3">{member.role}</p>

        <div>
          {expanded ? (
            <p className="text-theme-secondary mb-4">
              {member.bio.split('\n').map((paragraph, i) => (
                <span key={i}>
                  {paragraph}
                  {i < member.bio.split('\n').length - 1 && (
                    <>
                      <br />
                      <br />
                    </>
                  )}
                </span>
              ))}
            </p>
          ) : (
            <p className="text-theme-secondary mb-4">
              {bioSnippet}
              {needsExpansion && '...'}
            </p>
          )}

          {needsExpansion && (
            <button
              id={buttonId}
              onClick={() => setExpanded(!expanded)}
              className="text-secondary text-sm font-medium flex items-center hover:underline focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 focus:ring-offset-surface rounded-sm mb-4"
              aria-expanded={expanded}
              aria-controls={`${buttonId}-content`}
            >
              <span className="mr-1">
                {expanded ? 'Show less' : 'Read more'}
              </span>
              {expanded ? (
                <FiChevronUp aria-hidden="true" />
              ) : (
                <FiChevronDown aria-hidden="true" />
              )}
            </button>
          )}
        </div>

        <div className="flex space-x-3">
          {member.social.linkedin && (
            <a
              href={member.social.linkedin}
              className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors"
              aria-label={`${member.name}'s LinkedIn`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiLinkedin size={20} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamMemberItem;
